import { useState, useRef, useEffect, useCallback } from 'react';
import { voipTestApi, formatApiError } from '../lib/api';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  PhoneCall, 
  PhoneOff, 
  Send, 
  User, 
  Bot, 
  Loader2,
  UserCheck,
  UserX,
  Clock,
  Volume2,
  Square,
  Mic,
  Check,
  Upload,
  Trash2,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

// Clean markdown from AI text for display
function cleanMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*]\s+/gm, '');
}

function MessageBubble({ role, content, timestamp, onPlayAudio, isPlaying, isLoadingAudio }) {
  const isUser = role === 'user';
  return (
    <div 
      className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}
      data-testid={`message-${role}`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        isUser ? 'bg-sky-100' : 'bg-emerald-100'
      }`}>
        {isUser 
          ? <User className="w-4 h-4 text-sky-600" />
          : <Bot className="w-4 h-4 text-emerald-600" />
        }
      </div>
      <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
        isUser 
          ? 'bg-sky-500 text-white rounded-tr-md' 
          : 'bg-white border border-slate-200 text-slate-800 rounded-tl-md shadow-sm'
      }`}>
        <p className="text-sm leading-relaxed">{cleanMarkdown(content)}</p>
        <div className={`flex items-center gap-2 mt-1.5 ${isUser ? 'justify-end' : 'justify-between'}`}>
          {timestamp && (
            <p className={`text-xs ${isUser ? 'text-sky-100' : 'text-slate-400'}`}>
              {new Date(timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </p>
          )}
          {!isUser && onPlayAudio && (
            <button
              onClick={() => onPlayAudio(content)}
              disabled={isLoadingAudio}
              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full transition-all ${
                isPlaying 
                  ? 'bg-emerald-100 text-emerald-700' 
                  : 'bg-slate-100 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600'
              }`}
              data-testid="play-audio-btn"
            >
              {isLoadingAudio ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : isPlaying ? (
                <Square className="w-3 h-3" />
              ) : (
                <Volume2 className="w-3 h-3" />
              )}
              <span>{isLoadingAudio ? 'Carico...' : isPlaying ? 'Stop' : 'Ascolta'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function VoIPTest() {
  const [phone, setPhone] = useState('');
  const [callId, setCallId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [patientInfo, setPatientInfo] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [callActive, setCallActive] = useState(false);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [loadingAudioIndex, setLoadingAudioIndex] = useState(null);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('alloy');
  const [previewingVoice, setPreviewingVoice] = useState(null);
  const [playingPreview, setPlayingPreview] = useState(null);
  const [cloneName, setCloneName] = useState('');
  const [cloneDescription, setCloneDescription] = useState('');
  const [cloneFile, setCloneFile] = useState(null);
  const [cloning, setCloning] = useState(false);
  const [showClonePanel, setShowClonePanel] = useState(false);
  const [deletingVoice, setDeletingVoice] = useState(null);
  const messagesEndRef = useRef(null);
  const timerRef = useRef(null);
  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const previewAudioRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadVoices();
  }, []);

  const loadVoices = async () => {
    try {
      const res = await voipTestApi.getVoices();
      setVoices(res.data.voices);
      setSelectedVoice(res.data.selected_voice);
    } catch (err) {
      console.error('Failed to load voices:', err);
    }
  };

  const previewVoice = async (voiceId) => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    if (playingPreview === voiceId) {
      setPlayingPreview(null);
      return;
    }
    setPreviewingVoice(voiceId);
    try {
      const res = await voipTestApi.previewVoice(voiceId);
      const audio = new Audio(`data:audio/mpeg;base64,${res.data.audio_base64}`);
      previewAudioRef.current = audio;
      audio.onended = () => { setPlayingPreview(null); previewAudioRef.current = null; };
      audio.onerror = () => { setPlayingPreview(null); previewAudioRef.current = null; };
      setPlayingPreview(voiceId);
      setPreviewingVoice(null);
      await audio.play();
    } catch (err) {
      toast.error(formatApiError(err));
      setPreviewingVoice(null);
    }
  };

  const selectVoice = async (voiceId) => {
    try {
      const res = await voipTestApi.selectVoice(voiceId);
      setSelectedVoice(voiceId);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cloneVoice = async () => {
    if (!cloneName.trim()) {
      toast.error('Inserisci un nome per la voce');
      return;
    }
    if (!cloneFile) {
      toast.error('Seleziona un file audio');
      return;
    }
    setCloning(true);
    try {
      const formData = new FormData();
      formData.append('name', cloneName.trim());
      formData.append('description', cloneDescription.trim() || `Voce clonata - ${cloneName.trim()}`);
      formData.append('file', cloneFile);
      const res = await voipTestApi.cloneVoice(formData);
      toast.success(res.data.message);
      setCloneName('');
      setCloneDescription('');
      setCloneFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setShowClonePanel(false);
      await loadVoices();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCloning(false);
    }
  };

  const deleteClonedVoice = async (voiceId) => {
    setDeletingVoice(voiceId);
    try {
      const res = await voipTestApi.deleteClonedVoice(voiceId);
      toast.success(res.data.message);
      if (selectedVoice === voiceId) {
        setSelectedVoice(voices[0]?.id);
      }
      await loadVoices();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeletingVoice(null);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (callActive) {
      timerRef.current = setInterval(() => {
        setCallDuration(d => d + 1);
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [callActive]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (previewAudioRef.current) { previewAudioRef.current.pause(); previewAudioRef.current = null; }
    };
  }, []);

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const playAudio = useCallback(async (text, index) => {
    // If already playing this message, stop it
    if (playingIndex === index) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingIndex(null);
      return;
    }

    // Stop any current playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    setLoadingAudioIndex(index);
    try {
      const res = await voipTestApi.generateTTS(text);
      const audioData = res.data.audio_base64;
      const audio = new Audio(`data:audio/mpeg;base64,${audioData}`);
      audioRef.current = audio;
      
      audio.onended = () => {
        setPlayingIndex(null);
        audioRef.current = null;
      };
      audio.onerror = () => {
        setPlayingIndex(null);
        audioRef.current = null;
        toast.error('Errore nella riproduzione audio');
      };

      setPlayingIndex(index);
      setLoadingAudioIndex(null);
      await audio.play();
    } catch (err) {
      toast.error(formatApiError(err));
      setLoadingAudioIndex(null);
    }
  }, [playingIndex]);

  const startCall = async () => {
    if (!phone.trim()) {
      toast.error('Inserisci un numero di telefono');
      return;
    }
    setLoading(true);
    try {
      const res = await voipTestApi.startCall(phone.trim());
      setCallId(res.data.call_id);
      setCallActive(true);
      setCallDuration(0);
      setPatientInfo({
        found: res.data.patient_found,
        name: res.data.patient_name
      });
      setMessages([{
        role: 'assistant',
        content: res.data.greeting,
        timestamp: new Date().toISOString()
      }]);
      toast.success('Chiamata simulata avviata');
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !callId) return;
    const msg = inputMessage.trim();
    setInputMessage('');
    setSendingMessage(true);

    setMessages(prev => [...prev, {
      role: 'user',
      content: msg,
      timestamp: new Date().toISOString()
    }]);

    try {
      const res = await voipTestApi.sendMessage(callId, msg);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response,
        timestamp: new Date().toISOString()
      }]);
      if (res.data.appointment_created) {
        toast.success('Appuntamento creato dalla conversazione AI!');
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingMessage(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const endCall = async () => {
    if (!callId) return;
    try {
      await voipTestApi.endCall(callId);
      setCallActive(false);
      clearInterval(timerRef.current);
      toast.info(`Chiamata terminata - Durata: ${formatDuration(callDuration)}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetCall = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setCallId(null);
    setMessages([]);
    setPatientInfo(null);
    setCallActive(false);
    setCallDuration(0);
    setInputMessage('');
    setPlayingIndex(null);
    setLoadingAudioIndex(null);
    clearInterval(timerRef.current);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Layout>
      <PageHeader 
        title="Simulatore VoIP" 
        subtitle="Testa il flusso di prenotazione vocale AI"
      />
      
      <div className="content-area" data-testid="voip-test-panel">
        <div className="max-w-3xl mx-auto">
          {/* Voice Selector */}
          {!callId && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-4" data-testid="voice-selector">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Mic className="w-4 h-4 text-sky-600" />
                  <h3 className="font-semibold text-slate-800 text-sm" style={{ fontFamily: 'Manrope' }}>
                    Voce Assistente AI
                  </h3>
                </div>
                <button
                  onClick={() => setShowClonePanel(!showClonePanel)}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-all font-medium ${
                    showClonePanel 
                      ? 'bg-violet-100 text-violet-700 border border-violet-200' 
                      : 'bg-slate-100 text-slate-600 hover:bg-violet-50 hover:text-violet-600 border border-slate-200'
                  }`}
                  data-testid="toggle-clone-panel-btn"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  {showClonePanel ? 'Chiudi' : 'Clona Voce'}
                </button>
              </div>

              {/* Voice Clone Panel */}
              {showClonePanel && (
                <div className="mb-4 bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200 rounded-xl p-4" data-testid="voice-clone-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-violet-600" />
                    <h4 className="font-semibold text-violet-800 text-sm">Clona una Voce Personalizzata</h4>
                  </div>
                  <p className="text-xs text-violet-600/70 mb-4">
                    Carica un audio di almeno 30 secondi (ideale 1 minuto) della persona da clonare. Parlato chiaro, senza rumori di fondo.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-medium text-slate-700 mb-1 block">Nome voce *</label>
                      <Input
                        value={cloneName}
                        onChange={(e) => setCloneName(e.target.value)}
                        placeholder="es. Maria Receptionist"
                        className="bg-white text-sm"
                        data-testid="clone-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-700 mb-1 block">Descrizione (opzionale)</label>
                      <Input
                        value={cloneDescription}
                        onChange={(e) => setCloneDescription(e.target.value)}
                        placeholder="es. Voce della receptionist del centro medico"
                        className="bg-white text-sm"
                        data-testid="clone-description-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-700 mb-1 block">File audio * (MP3, WAV, M4A)</label>
                      <div className="flex items-center gap-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="audio/*,.mp3,.wav,.m4a,.ogg,.webm"
                          onChange={(e) => setCloneFile(e.target.files[0])}
                          className="text-xs text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-violet-100 file:text-violet-700 hover:file:bg-violet-200 file:cursor-pointer"
                          data-testid="clone-file-input"
                        />
                      </div>
                      {cloneFile && (
                        <p className="text-xs text-slate-500 mt-1">
                          {cloneFile.name} ({(cloneFile.size / 1024 / 1024).toFixed(1)} MB)
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={cloneVoice}
                      disabled={cloning || !cloneName.trim() || !cloneFile}
                      className="w-full bg-violet-600 hover:bg-violet-700 text-white gap-2 mt-1"
                      data-testid="clone-voice-btn"
                    >
                      {cloning ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Clonazione in corso...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Clona Voce
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {voices.map(v => {
                  const isSelected = selectedVoice === v.id;
                  const isPreviewing = previewingVoice === v.id;
                  const isPlaying = playingPreview === v.id;
                  const isCloned = v.is_cloned;
                  const isDeleting = deletingVoice === v.id;
                  return (
                    <div 
                      key={v.id} 
                      className={`relative border rounded-lg p-3 transition-all cursor-pointer ${
                        isSelected 
                          ? isCloned
                            ? 'border-violet-400 bg-violet-50 ring-1 ring-violet-200'
                            : 'border-sky-400 bg-sky-50 ring-1 ring-sky-200' 
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid={`voice-${v.id}`}
                    >
                      {isSelected && (
                        <div className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center ${
                          isCloned ? 'bg-violet-500' : 'bg-sky-500'
                        }`}>
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <div onClick={() => selectVoice(v.id)}>
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium text-sm text-slate-800">{v.name}</p>
                          {isCloned && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-600 font-medium">
                              <Sparkles className="w-2.5 h-2.5" />
                              Clonata
                            </span>
                          )}
                        </div>
                        <p className={`text-xs mt-0.5 ${
                          isCloned ? 'text-violet-500' :
                          v.gender === 'Femminile' ? 'text-pink-500' : 
                          v.gender === 'Maschile' ? 'text-blue-500' : 'text-slate-500'
                        }`}>
                          {v.gender}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">{v.description}</p>
                      </div>
                      <div className="flex gap-1 mt-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); previewVoice(v.id); }}
                          className={`flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded-md transition-colors ${
                            isPlaying 
                              ? 'bg-sky-100 text-sky-700' 
                              : 'bg-slate-100 text-slate-500 hover:bg-sky-50 hover:text-sky-600'
                          }`}
                          disabled={isPreviewing}
                          data-testid={`preview-${v.id}`}
                        >
                          {isPreviewing ? <Loader2 className="w-3 h-3 animate-spin" /> : 
                           isPlaying ? <Square className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                          {isPreviewing ? 'Carico...' : isPlaying ? 'Stop' : 'Ascolta'}
                        </button>
                        {isCloned && (
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteClonedVoice(v.id); }}
                            className="flex items-center justify-center gap-1 text-xs py-1.5 px-2.5 rounded-md bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 transition-colors"
                            disabled={isDeleting}
                            data-testid={`delete-voice-${v.id}`}
                          >
                            {isDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Call Setup or Active Call Header */}
          {!callId ? (
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="call-setup">
              <h3 className="font-semibold text-slate-800 mb-1" style={{ fontFamily: 'Manrope' }}>
                Avvia Chiamata Simulata
              </h3>
              <p className="text-sm text-slate-500 mb-5">
                Inserisci il numero di un paziente per simulare una chiamata in arrivo e testare il flusso AI.
              </p>
              <div className="flex gap-3">
                <Input
                  placeholder="+39 333 1234567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && startCall()}
                  className="flex-1"
                  data-testid="phone-input"
                />
                <Button
                  onClick={startCall}
                  disabled={loading}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
                  data-testid="start-call-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />}
                  Chiama
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Active Call Header */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-4" data-testid="call-header">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      patientInfo?.found ? 'bg-emerald-100' : 'bg-amber-100'
                    }`}>
                      {patientInfo?.found 
                        ? <UserCheck className="w-5 h-5 text-emerald-600" />
                        : <UserX className="w-5 h-5 text-amber-600" />
                      }
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 text-sm">
                        {patientInfo?.found ? patientInfo.name : 'Paziente sconosciuto'}
                      </p>
                      <p className="text-xs text-slate-500">{phone}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {callActive && (
                      <div className="flex items-center gap-2 text-sm" data-testid="call-timer">
                        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <Clock className="w-3.5 h-3.5 text-slate-400" />
                        <span className="font-mono text-slate-600">{formatDuration(callDuration)}</span>
                      </div>
                    )}
                    {callActive ? (
                      <Button
                        onClick={endCall}
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50 gap-1.5"
                        data-testid="end-call-btn"
                      >
                        <PhoneOff className="w-4 h-4" />
                        Termina
                      </Button>
                    ) : (
                      <Button
                        onClick={resetCall}
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        data-testid="new-call-btn"
                      >
                        <PhoneCall className="w-4 h-4" />
                        Nuova chiamata
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Chat Area */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="chat-area">
                <div className="h-[420px] overflow-y-auto p-5 space-y-4">
                  {/* System message */}
                  <div className="text-center">
                    <span className="inline-flex items-center gap-1.5 text-xs text-slate-400 bg-white px-3 py-1 rounded-full border border-slate-100">
                      <PhoneCall className="w-3 h-3" />
                      Chiamata simulata avviata - {phone}
                    </span>
                  </div>

                  {messages.map((msg, i) => (
                    <MessageBubble 
                      key={i} 
                      {...msg} 
                      onPlayAudio={msg.role === 'assistant' ? (text) => playAudio(text, i) : undefined}
                      isPlaying={playingIndex === i}
                      isLoadingAudio={loadingAudioIndex === i}
                    />
                  ))}

                  {sendingMessage && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-md px-4 py-3 shadow-sm">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}

                  {!callActive && messages.length > 0 && (
                    <div className="text-center">
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-400 bg-white px-3 py-1 rounded-full border border-slate-100">
                        <PhoneOff className="w-3 h-3" />
                        Chiamata terminata - {formatDuration(callDuration)}
                      </span>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                {callActive && (
                  <div className="border-t border-slate-200 bg-white p-3">
                    <div className="flex gap-2">
                      <Input
                        ref={inputRef}
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Scrivi come se fossi il paziente al telefono..."
                        disabled={sendingMessage}
                        className="flex-1"
                        data-testid="message-input"
                      />
                      <Button
                        onClick={sendMessage}
                        disabled={sendingMessage || !inputMessage.trim()}
                        className="bg-sky-500 hover:bg-sky-600 text-white"
                        data-testid="send-message-btn"
                      >
                        {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-400 mt-2 px-1">
                      Premi Invio per inviare. Simula le risposte del paziente per testare il flusso AI.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
